// Upryzze\frontend\packages\business-console\src\utils\contentEditorHelpers.js

// frontend/packages/business-console/src/utils/contentEditorHelpers.js

import yaml from 'js-yaml';

export const isProduction = process.env.NODE_ENV === 'production';

export const devWarn = (...args) => {
  if (isProduction) return;
  // eslint-disable-next-line no-console
  console.warn(...args);
};

export const safeString = (v) =>
  v === undefined || v === null ? '' : String(v);

export const safeJsonParse = (value, fallback = null) => {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/* ----------------------------- Frontmatter ----------------------------- */

export function parseFrontmatterYaml(text) {
  const raw = safeString(text);
  if (!raw.trim()) return {};

  const data = yaml.load(raw);
  return data && typeof data === 'object' ? data : {};
}

export function buildFrontmatterYaml(obj) {
  // Use double quotes: the tenant site repos run prettier with
  // `singleQuote: false`, so single-quoted scalars (js-yaml's default, e.g.
  // empty strings -> '') fail their `format:check` in the deploy verify step.
  // Double quotes (or unquoted, where allowed) keep console-authored content
  // prettier-clean. See contentEditorHelpers contentSitePath note + the
  // site repos' .prettierrc.cjs.
  return yaml
    .dump(obj || {}, { lineWidth: 120, noRefs: true, quotingType: '"' })
    .trimEnd();
}

export function splitFrontmatter(raw) {
  const input = safeString(raw);

  if (!input.startsWith('---')) return { frontmatter: '', body: input };

  const end = input.indexOf('\n---', 3);
  if (end === -1) return { frontmatter: '', body: input };

  const fm = input.slice(3, end).trim();
  const body = input.slice(end + '\n---'.length).replace(/^\s*\n/, '');
  return { frontmatter: fm, body };
}

export function recombine(frontmatter, body) {
  const fm = safeString(frontmatter).trim();
  const b = safeString(body);
  if (!fm) return b.trimStart();
  return `---\n${fm}\n---\n\n${b.trimStart()}`;
}

/* ---------------------------- Dot-path utils --------------------------- */
//
// Immutable get/has/set for nested values addressed by a dot-delimited path.
// Numeric segments (e.g. "0", "1") indicate array indexes — both when reading
// and when writing. Existing arrays are cloned as arrays so downstream
// `Array.isArray` checks (e.g. ArrayObjectField) keep passing after a write.
//
// Path grammar: "a.b.0.c". Bracket notation ("a[0].c") is intentionally not
// supported — it is not used anywhere in this codebase and keeping the
// grammar narrow avoids ambiguity with object keys that contain brackets.

function parsePath(path) {
  return safeString(path).split('.').filter(Boolean);
}

// Matches non-negative integer segments without leading zeros — same rule
// lodash uses to disambiguate array indexes from string keys.
function isIndex(segment) {
  return /^(0|[1-9]\d*)$/.test(segment);
}

// Shallow clone that preserves array vs. plain-object identity. Returns null
// for primitives / null / undefined so callers can decide what container to
// create.
function shallowCloneNode(node) {
  if (Array.isArray(node)) return node.slice();
  if (node && typeof node === 'object') return { ...node };
  return null;
}

export function hasPath(obj, path) {
  const parts = parsePath(path);
  let cur = obj;

  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return false;
    if (!Object.prototype.hasOwnProperty.call(cur, p)) return false;
    cur = cur[p];
  }

  return true;
}

export function getAtPath(obj, path) {
  const parts = parsePath(path);
  let cur = obj;

  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }

  return cur;
}

export function setAtPath(obj, path, value) {
  const parts = parsePath(path);
  const root = shallowCloneNode(obj) ?? {};
  if (parts.length === 0) return root;

  let cur = root;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const next = parts[i + 1];

    let child = shallowCloneNode(cur[key]);
    if (child == null) child = isIndex(next) ? [] : {};

    cur[key] = child;
    cur = child;
  }

  cur[parts[parts.length - 1]] = value;
  return root;
}

/* -------------------------- Schema validation -------------------------- */
/**
 * Your schema format (as used by current backend):
 * {
 *   fields: {
 *     title: { type: "string", required: true },
 *     tags: { type: "array", items: { type: "string" }, required: false },
 *     seo: { type: "object", fields: { ... }, required: false },
 *     ...
 *   }
 * }
 *
 * We validate required fields recursively and return a list of missing field paths.
 */
export function validateRequired(frontmatterObj, schemaFields) {
  if (!schemaFields) return [];

  const missing = [];

  const validateObject = (obj, schema, prefix = '') => {
    for (const [key, spec] of Object.entries(schema || {})) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const value = obj?.[key];

      if (spec?.required) {
        if (value === undefined || value === null) {
          missing.push(fullKey);
          continue;
        }

        if (spec.type === 'string' && safeString(value).trim() === '') {
          missing.push(fullKey);
          continue;
        }

        if (
          spec.type === 'array' &&
          Array.isArray(value) &&
          value.length === 0
        ) {
          missing.push(fullKey);
          continue;
        }
      }

      if (
        spec?.type === 'object' &&
        spec?.fields &&
        value !== null &&
        typeof value === 'object'
      ) {
        validateObject(value, spec.fields, fullKey);
      }
    }
  };

  validateObject(frontmatterObj || {}, schemaFields);
  return missing;
}

/* ------------------------- Filename normalization ---------------------- */

export function isSafeFilename(name) {
  // Allow: letters, numbers, hyphen, underscore
  return /^[a-zA-Z0-9-_]+$/.test(safeString(name).trim());
}

export function normalizeFilename(name) {
  // Normalize common user input to a safe-ish filename
  return safeString(name)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '')
    .replace(/-+/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '');
}
//used to remove trailing ".md" from file names
export function stripMarkdownExtension(filename) {
  return safeString(filename).replace(/\.md$/i, '');
}

/**
 * Create a content path consistent with backend expectations.
 * Example: src/content/blog/my-post.md
 */
export function buildContentPath(collection, filenameNoExt) {
  const col = safeString(collection).trim();
  const file = safeString(filenameNoExt).trim();

  if (!col) throw new Error('Collection is required.');
  if (!file) throw new Error('Filename is required.');

  const normalized = normalizeFilename(file);
  if (!normalized || !isSafeFilename(normalized)) {
    throw new Error(
      'Filename must be letters, numbers, hyphens, or underscores.'
    );
  }

  return `src/content/${col}/${normalized}.md`;
}

// ---------------------------------------------------------------------------
// Label humanization
// ---------------------------------------------------------------------------

/**
 * Well-known label overrides for collection names.
 * Add entries here when the generic camelCase/kebab → Title Case
 * conversion doesn't produce the right result (e.g. acronyms).
 */
const LABEL_OVERRIDES = {
  faqs: 'FAQs',
  faq: 'FAQ',
  serviceAreas: 'Service Areas',
  service_areas: 'Service Areas',
};

/**
 * Collections that should only ever have a single file (e.g. site settings).
 * Used by FileList to hide the "+ New" button.
 */
export const SINGLETON_COLLECTIONS = new Set(['settings']);

/**
 * Best-effort map from a content FILE to the public site PATH it most
 * likely renders as, so per-page analytics (views/leads/SEO, keyed by
 * URL path) can be joined to the editable file list.
 *
 * There is NO stored file -> route mapping (content lives in GitHub, routes
 * are decided by the site template), so this is a documented heuristic and
 * the UI shows "no traffic yet" when a file does not confidently resolve or
 * has no metrics:
 *   - strip the extension, lowercase
 *   - drop the "src/content/" content-root prefix the file list carries
 *     (the API returns the full repo path, e.g. src/content/blog/post.md)
 *   - drop a leading "pages/" segment (pages are top-level routes)
 *   - drop a trailing "index" or "home" segment (so it collapses to its
 *     parent, e.g. pages/index -> "/", blog/index -> "/blog")
 *
 * Examples:
 *   src/content/pages/services.md -> /services
 *   src/content/pages/index.md    -> /
 *   src/content/blog/my-post.md   -> /blog/my-post
 *   home.md                       -> /
 *
 * @param {{path?: string, name?: string}} file
 * @returns {string} canonical site path, or "" if it cannot resolve.
 */
export function contentSitePath(file) {
  const raw = String((file && (file.path || file.name)) || '').trim();
  if (!raw) return '';
  const noExt = raw.replace(/\.(md|mdx|markdown)$/i, '').toLowerCase();
  let segs = noExt.split('/').filter(Boolean);
  // The content list returns full repo paths (src/content/<collection>/...);
  // the public route is relative to the content root, so drop that prefix.
  if (segs[0] === 'src') segs = segs.slice(1);
  if (segs[0] === 'content') segs = segs.slice(1);
  if (segs[0] === 'pages') segs = segs.slice(1);
  const last = segs[segs.length - 1];
  if (last === 'index' || last === 'home') segs = segs.slice(0, -1);
  const path = `/${segs.join('/')}`;
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Convert a camelCase, kebab-case, or snake_case identifier to a
 * human-readable Title Case label.
 *
 * Examples:
 *   humanizeLabel("serviceAreas")       → "Service Areas"
 *   humanizeLabel("plumbing-services")  → "Plumbing Services"
 *   humanizeLabel("faqs")              → "FAQs"  (via LABEL_OVERRIDES)
 *
 * @param {string} key - The raw identifier to humanize.
 * @returns {string} A human-readable label.
 */
export function humanizeLabel(key) {
  const k = String(key || '').trim();
  if (LABEL_OVERRIDES[k]) return LABEL_OVERRIDES[k];
  return k
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ---------------------------------------------------------------------------
 * Prose extraction
 *
 * Two AI surfaces need the same answer to "which parts of this document are
 * copy a writer would edit?": the in-editor copilot (which sends fields to be
 * rewritten) and SEO assist (which summarizes the page).
 *
 * Getting it wrong has two failure modes we hit in practice:
 *   - Sending structural fields (a `cld:` media token, a slug, a date, a URL,
 *     a layout name) invites the model to rewrite them, and a one-click Apply
 *     then writes a broken value into the draft.
 *   - Ignoring frontmatter entirely leaves the structured pages (a home or
 *     services page whose copy lives ONLY in frontmatter, with an empty
 *     markdown body) with nothing to work on, which is why SEO assist used to
 *     sit permanently disabled on exactly the pages that need it most.
 * ------------------------------------------------------------------------- */

// Keys that are never prose, whatever they contain. Structural or routing
// values that a rewrite would corrupt.
const NON_PROSE_KEYS = new Set([
  'slug',
  'permalink',
  'url',
  'href',
  'layout',
  'template',
  'type',
  'collection',
  'category',
  'draft',
  'published',
  'order',
  'weight',
  'icon',
  'image',
  'coverimage',
  'ogimage',
  'thumbnail',
  'video',
  'date',
  'publishdate',
  'pubdate',
  'updateddate',
  'lastmod',
  'author',
  'id',
  'ref',
  'color',
  'theme',
]);

// Value shapes that are never prose either, whatever the key is called.
function looksStructural(value) {
  const v = String(value || '').trim();
  if (!v) return true;
  if (v.startsWith('cld:')) return true; // media token
  if (/^https?:\/\//i.test(v)) return true; // absolute URL
  if (/^\/\S*$/.test(v)) return true; // site-relative path
  if (/^#[0-9a-f]{3,8}$/i.test(v)) return true; // hex color
  if (/^\d{4}-\d{2}-\d{2}([T\s]|$)/.test(v)) return true; // ISO-ish date
  if (/^(true|false)$/i.test(v)) return true;
  if (/^-?\d+(\.\d+)?$/.test(v)) return true; // bare number
  return false;
}

/**
 * True when `value` under `key` is copy a writer would edit.
 * @param {string} key   frontmatter key (any case)
 * @param {unknown} value
 */
export function isProseField(key, value) {
  if (typeof value !== 'string') return false;
  const k = String(key || '')
    .trim()
    .toLowerCase();
  if (!k || NON_PROSE_KEYS.has(k)) return false;
  if (k.endsWith('image') || k.endsWith('url') || k.endsWith('date')) {
    return false;
  }
  return !looksStructural(value);
}

/**
 * The document's editable prose, body first.
 *
 * Body goes first deliberately: the backend caps how many fields it will take
 * (MAX_FIELDS in contentReviseService), and a page with many frontmatter
 * strings used to push `body` past that cap, so the copilot silently could not
 * touch the page content at all.
 *
 * @param {Record<string, unknown>} frontmatterObj
 * @param {string} markdownBody
 * @param {number} [limit] max entries to return, body included
 * @returns {Record<string, string>}
 */
export function collectProseFields(frontmatterObj, markdownBody, limit = 12) {
  const out = {};
  // Always send the body when there is a body field at all, EMPTY INCLUDED.
  // The backend only ever returns fields it was handed, so omitting an empty
  // body would mean "write an intro for this page" could not produce one on a
  // page that does not have one yet - the exact case where it is most useful.
  if (typeof markdownBody === 'string') out.body = markdownBody;

  for (const [key, value] of Object.entries(frontmatterObj || {})) {
    if (Object.keys(out).length >= limit) break;
    if (!isProseField(key, value)) continue;
    out[key] = value;
  }
  return out;
}
